import { useMemo } from 'react';
import { NEUTRAL_K99_WITHOUT_TOKEN, useTheme } from '@balance-web/theme';

import type { InputShapeType, InputSizeType } from './types';

export type InputBaseProps = {
  shape: InputShapeType;
  size: InputSizeType;
  focusRing?: 'normal' | 'bold';
};

/**
 * Rendered height of a non-textarea input per size. Also the line-height a
 * readonly text stand-in needs to stay vertically aligned with sibling inputs.
 */
export const inputBoxSizes: Record<InputSizeType, number> = {
  xsmall: 26,
  small: 32,
  medium: 40,
  large: 48,
};

export function useInputStyles({ shape, size }: InputBaseProps) {
  const { semantic, typography, radii, spacing } = useTheme();

  const { fontSizeText, borderRadius, boxSize, paddingX } = useMemo(() => {
    if (size === 'xsmall') {
      return {
        fontSizeText: typography.fontSize.small,
        borderRadius: radii.small,
        boxSize: inputBoxSizes.xsmall,
        paddingX: spacing.medium,
      };
    }

    if (size === 'small') {
      return {
        fontSizeText: typography.fontSize.small,
        borderRadius: radii.small,
        boxSize: inputBoxSizes.small,
        paddingX: spacing.medium,
      };
    }

    if (size === 'medium') {
      return {
        fontSizeText: typography.fontSize.medium,
        borderRadius: radii.medium,
        boxSize: inputBoxSizes.medium,
        paddingX: spacing.large,
      };
    }

    return {
      fontSizeText: typography.fontSize.large,
      borderRadius: radii.medium,
      boxSize: inputBoxSizes.large,
      paddingX: spacing.large,
    };
  }, [
    radii.medium,
    radii.small,
    size,
    spacing.large,
    spacing.medium,
    typography.fontSize.large,
    typography.fontSize.medium,
    typography.fontSize.small,
  ]);

  const borderColor = semantic.colour.surface.neutral.border;
  const focusStyles = {
    backgroundColor: semantic.colour.neutral.background,
    borderColor: semantic.colour.primary.background,
    boxShadow: `inset 0 0 0 2px ${semantic.colour.primary.background}, 0 0 0 0 transparent`,
  } as const;

  return {
    appearance: 'none',
    backgroundColor: semantic.colour.neutral.background,
    border: `0px solid ${borderColor}`,
    borderRadius: shape === 'round' ? 9999 : borderRadius,
    boxShadow: `inset 0 0 0 1px ${borderColor}, 0 0 0 0 transparent`,
    boxSizing: 'border-box',
    color: semantic.colour.text.textPrimary,
    fontFamily: typography.fontFamily.body,
    fontSize: fontSizeText,
    fontWeight: typography.fontWeight.regular,
    outline: 0,
    paddingLeft: paddingX,
    paddingRight: paddingX,
    textAlign: 'inherit',
    transition:
      'background-color 200ms cubic-bezier(0.2, 0, 0, 1), border-color 200ms cubic-bezier(0.2, 0, 0, 1), box-shadow 200ms cubic-bezier(0.2, 0, 0, 1)',
    width: '100%',

    // things that aren't textarea (input, select etc.)
    height: boxSize,
    lineHeight: `${boxSize}`, // fix vertical alignment of text
    paddingBottom: 0,
    paddingTop: 0,

    // alternate styles for textarea
    'textarea&': {
      lineHeight: typography.leading.tight,
      minHeight: boxSize,
      height: 'auto',
      paddingBottom: '0.66em',
      paddingTop: '0.66em',
      resize: 'vertical',
    },

    ':hover': {
      backgroundColor: NEUTRAL_K99_WITHOUT_TOKEN,
      borderColor: semantic.colour.surface.neutral.borderStrong,
      boxShadow: `inset 0 0 0 1px ${semantic.colour.surface.neutral.borderStrong}, 0 0 0 0 transparent`,
    },
    ':active': focusStyles,
    ':focus': focusStyles,

    // disabled state
    ':disabled': {
      backgroundColor: NEUTRAL_K99_WITHOUT_TOKEN,
      color: semantic.colour.text.textDisabled,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },

    '&[readonly]': {
      borderColor: '#FFFFFF',
      backgroundColor: '#FFFFFF',
      boxShadow: 'none',
      paddingLeft: 0,

      ':active, :focus': {
        borderColor: '#FFFFFF',
        backgroundColor: '#FFFFFF',
        boxShadow: 'none',
      },
    },

    // invalid state
    // NOTE: the data attribute is specifically for the current date-picker
    // implementation where we use a button, so `aria-invalid` is not
    // allowed/supported
    '&[data-invalid=true], &[aria-invalid=true]': {
      borderColor: semantic.colour.reserved.critical.background,
      boxShadow: `inset 0 0 0 1px ${semantic.colour.reserved.critical.background}, 0 0 0 0 transparent`,

      ':focus': {
        boxShadow: `inset 0 0 0 2px ${semantic.colour.reserved.critical.background}, 0 0 0 0 transparent`,
      },
    },

    // placeholder text
    '&::placeholder': {
      color: semantic.colour.text.textPlaceholder,
    },
  } as const;
}
